/**
 * useHomeRoute — resolves the canonical "home" route for the current user.
 *
 * Returns one of:
 *   /platform-admin, /school-admin, /school-teacher, /school-parent,
 *   /child/:cid/home, /family, /set-pin, /parent/home (fallback)
 *
 * Resolution: fetches /api/auth/me on first call, then defers to
 * `routeByRole(user)` which prefers the backend-computed `next_route` hint
 * and falls back to client-side role logic.
 *
 * Use case: any "back to home" / "exit" button on shared pages used by
 * multiple personas (e.g. school parents + consumer parents both visit
 * ParentChatView). Replaces hardcoded `navigate('/parent/home')` calls.
 */
import { useEffect, useState } from 'react';
import api from '@/lib/api';
import routeByRole from '@/utils/routeByRole';

const SAFE_DEFAULT = '/parent/home';

export default function useHomeRoute() {
  // Sensible default while /auth/me is in flight — keeps existing behaviour
  // for consumer parents (the vast majority of callers).
  const [route, setRoute] = useState(SAFE_DEFAULT);

  useEffect(() => {
    let mounted = true;
    api
      .get('/auth/me')
      .then((res) => {
        if (!mounted) return;
        const resolved = routeByRole(res.data) || SAFE_DEFAULT;
        setRoute(resolved);
      })
      .catch(() => {
        // Auth failure / network blip — keep safe default; the route guard
        // will redirect the user to /login if needed.
      });
    return () => {
      mounted = false;
    };
  }, []);

  return route;
}
